import { useMemo, useState } from "react";
import PopOnMe from "../../../../components/elements/poponme";
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import GTable from "../../../../components/elements/GTable";
import useModal from "../../../../hooks/modalHook";
import Loader from "../../../../components/Loader";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, GitGraph, Trash } from "lucide-react";
import TabContent from "../../../../components/tabs/TabContent";
import TabNavItem from "../../../../components/tabs/TabNavItem";
import Button from "../../../../components/form-control/Button";
import useBranch from "../../../../hooks/branchHook";


const Branch = ({ branchesProp, isSettings = false }: Props) => {
  const { ModalComponent } = useModal();
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'all' | 'active'>('all')
  const [searchString, setSearchString] = useState<string>();
  const { createNewBranchHandler, BranchModal, getBranchlist, updateBranchHandler, deleteBranchHandler } = useBranch();
  const invalidateQueries = () => { queryClient.invalidateQueries({ queryKey: ['getBranchlist'] }) }

  const { data: branches, isLoading } = useQuery({ queryKey: ['getBranchlist', searchString, activeTab], queryFn: () => getBranchlist(0, 50, activeTab === 'active', undefined, searchString) })

  const getTableRowActions = (branch: IBranch) => {
    const actions = [];

    actions.push(
      {
        label: 'Edit',
        icon: <Edit className="h-4 w-4" />,
        callback: () => {
          updateBranchHandler(branch);
        },
      },
      {
        label: 'Delete',
        icon: <Trash className="h-4 w-4" />,
        callback: () => {
          deleteBranchHandler(branch);
        },
      },

    );


    return actions;
  };

  const columns = useMemo(() => [
    {
      Header: 'M',
      columns: [
        {
          Header: 'Branch Name',
          accessor: 'name',
          Cell: (row: any) => {
            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
          }
        },
        {
          Header: 'Branch Manager',
          accessor: 'manager',
          Cell: (row: any) => {
            const manager = row.cell.value;
            const displayName = manager?.name
              ?? [manager?.firstName, manager?.lastName].filter(Boolean).join(' ');
            return <span>{displayName ? displayName : '-'}</span>;
          }
        },
        {
          Header: 'Phone Number',
          accessor: 'phone',
          Cell: (row: any) => {
            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
          }
        },
        {
          Header: 'Email Address',
          accessor: 'email',
          Cell: (row: any) => {
            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
          }
        },
        {
          Header: 'Location',
          accessor: 'address',
          Cell: (row: any) => {
            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
          }
        },
        {
          Header: 'Status',
          accessor: 'active',
          Cell: (row: any) => {
            return (
              <div className="">
                {row.cell.value ? (
                  <div className="px-2 border  border-[#0F7125] text-xs flex w-fit items-center justify-center text-[#0F7125] rounded-full ">
                    Active
                  </div>
                ) : (
                  <div className=" px-2 border border-[#FBC02D] text-xs flex w-fit items-center justify-center text-[#FBC02D] rounded-full ">
                    <span>Inactive</span>
                  </div>
                )}
              </div>
            );
          }
        },
        {
          Header: 'Action',
          id: 'Action',
          Cell: (row: any) => {
            return (
              <div className="cursor-pointer flex justify-center">
                <PopOnMe actions={getTableRowActions(row.row.original)}>
                  <EllipsisVerticalIcon className="h-4 w-4" />
                </PopOnMe>
              </div>
            );
          }
        }
      ]
    }
  ],
    []
  );

  const handleSetTab = (id: string) => {
    setActiveTab(id as 'all' | 'active');
  };

  return (
    <div className='p-5 bg-white dark:bg-darkBg min-h-full'>
      {isLoading ?

        <Loader message="Fetching all branches. Please wait..." brollyLoaderFixed />

        :
        <div className="overflow-x-auto mt-5 bg-white dark:bg-darkBg">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <div className="flex border-b border-gray-100 dark:border-darkBg-border">
              <TabNavItem
                id="all"
                title="All"
                activeTab={activeTab}
                setActiveTab={handleSetTab}
                classes="text-sm"
              />
              <TabNavItem
                id="active"
                title="Active"
                activeTab={activeTab}
                setActiveTab={handleSetTab}
                classes="text-sm"
              />
            </div>
            {!isSettings && <div className="flex justify-end md:pt-0">
              <Button
                label="New Branch"
                icon={<GitGraph className="w-5 h-5 mr-3" />}
                onClick={createNewBranchHandler}
                curved
                bgColor="primary"
                className=""
              />

            </div>}
          </div>
          {<div>

            {branchesProp ? <div>
              <TabContent id="all" activeTab={activeTab}>
                <GTable
                  columns={columns}
                  data={branchesProp ?? []}
                  isRowLinked={false}
                  onSearch={(searchString: string) => { setSearchString(searchString) }}
                  tableType={'branches'}
                  classes={'max-h-[400px]'}
                  stickyHeader
                  loading={isLoading}
                />
              </TabContent>
              <TabContent id="active" activeTab={activeTab}>
                <GTable
                  columns={columns}
                  data={branches ?? []}
                  isRowLinked={false}
                  tableType={'branches'}
                  onSearch={(searchString: string) => { setSearchString(searchString) }}
                  classes={'max-h-[400px]'}
                  stickyHeader
                />
              </TabContent>
            </div> :
              <div>
                <TabContent id="all" activeTab={activeTab}>
                  <GTable
                    columns={columns}
                    data={branches ?? []}
                    isRowLinked={false}
                    onSearch={(searchString: string) => { setSearchString(searchString) }}
                    tableType={'branches'}
                    classes={'max-h-[400px]'}
                    stickyHeader
                    loading={isLoading}
                  />
                </TabContent>
                <TabContent id="active" activeTab={activeTab}>
                  <GTable
                    columns={columns}
                    data={branches ?? []}
                    isRowLinked={false}
                    tableType={'branches'}
                    onSearch={(searchString: string) => { setSearchString(searchString) }}
                    classes={'max-h-[400px]'}
                    stickyHeader
                  />
                </TabContent>
              </div>
            }

          </div>}
        </div>
      }

      <ModalComponent />
      <BranchModal />
    </div>
  )
}

export default Branch;


export interface IBranch {
  active: boolean;
  address: string;
  email: string;
  name: string;
  phone: string;
  id: number;
  manager: any;
}

interface Props {
  branchesProp?: any;
  isSettings?: boolean;
}
